import { ErrorMessage, Field } from "formik";
import PropTypes from "prop-types";

import SoftBox from "components/SoftBox";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";
import CountrySelectPhoneInput from "../CountrySelectPhoneInput";

const rowSx = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", sm: "minmax(140px, 32%) minmax(0, 1fr)" },
  alignItems: "center",
  gap: { xs: 0.75, sm: 2 },
  py: 1.5,
  borderBottom: "1px solid rgba(140, 150, 170, 0.15)",
};

function InlineField({ label, name, type = "text", placeholder, disabled, phone, value, setFieldValue }) {
  return (
    <SoftBox sx={rowSx}>
      <SoftTypography component="label" htmlFor={name} variant="button" color="text" fontWeight="medium">
        {label}
      </SoftTypography>
      <SoftBox sx={{ minWidth: 0 }}>
        {phone ? (
          <CountrySelectPhoneInput
            name={name}
            id={name}
            value={value}
            setFieldValue={setFieldValue}
            disabled={disabled}
          />
        ) : (
          <Field
            as={SoftInput}
            id={name}
            name={name}
            type={type}
            placeholder={placeholder}
            disabled={disabled}
            fullWidth
          />
        )}
        <SoftTypography component="div" variant="caption" color="error" sx={{ mt: 0.5 }}>
          <ErrorMessage name={name} />
        </SoftTypography>
      </SoftBox>
    </SoftBox>
  );
}

function CompanyInfo({ formData, companyOwner }) {
  const { values, hasEditCompanyInfoAccess, isOwner, setFieldValue } = formData;
  const disabled = !isOwner && !hasEditCompanyInfoAccess;

  return (
    <SoftBox>
      <InlineField label="Company name" name="company_name" value={values.company_name} disabled={disabled} />
      <SoftBox sx={rowSx}>
        <SoftTypography variant="button" color="text" fontWeight="medium">Company owner</SoftTypography>
        <SoftTypography variant="button" className="white-to-dark">
          {companyOwner ? `${companyOwner.first_name} ${companyOwner.last_name}` : "—"}
        </SoftTypography>
      </SoftBox>
      <InlineField
        label="Phone"
        name="business_phone"
        value={values.business_phone}
        setFieldValue={setFieldValue}
        phone
        disabled={disabled}
      />
      <InlineField label="Email" name="email_address" type="email" value={values.email_address} disabled={disabled} />
      <SoftBox sx={{ ...rowSx, alignItems: "start", borderBottom: "none" }}>
        <SoftTypography variant="button" color="text" fontWeight="medium" sx={{ pt: 1 }}>
          Location
        </SoftTypography>
        <SoftBox sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" }, gap: 1.5 }}>
          <SoftBox sx={{ gridColumn: "1 / -1" }}>
            <InlineLocationField label="Address line 1" name="address_line_1" disabled={disabled} />
          </SoftBox>
          <SoftBox sx={{ gridColumn: "1 / -1" }}>
            <InlineLocationField label="Address line 2" name="address_line_2" disabled={disabled} />
          </SoftBox>
          <InlineLocationField label="City" name="city" disabled={disabled} />
          <InlineLocationField label="State" name="state" disabled={disabled} />
          <InlineLocationField label="Country" name="country" disabled={disabled} />
          <InlineLocationField label="ZIP code" name="zip" disabled={disabled} />
        </SoftBox>
      </SoftBox>
    </SoftBox>
  );
}

function InlineLocationField({ label, name, disabled }) {
  return (
    <SoftBox sx={{ minWidth: 0 }}>
      <SoftTypography component="label" htmlFor={name} variant="caption" color="text" sx={{ display: "block", mb: 0.5 }}>
        {label}
      </SoftTypography>
      <Field as={SoftInput} id={name} name={name} disabled={disabled} fullWidth />
      <SoftTypography component="div" variant="caption" color="error" sx={{ mt: 0.5 }}>
        <ErrorMessage name={name} />
      </SoftTypography>
    </SoftBox>
  );
}

CompanyInfo.propTypes = {
  formData: PropTypes.object.isRequired,
  companyOwner: PropTypes.object,
};

export default CompanyInfo;
